/**
 * @fullvsl/app-shell/store — in-memory VSL project store (FUL-225/244).
 *
 * Provides a mock backend for the app shell: CRUD over VslProject entities,
 * seeded with a sample VSL preloaded with the Full Funnel fixture MP4. Persists
 * to localStorage so config edits survive a page reload (privacy-light: no PII,
 * only the project config blob — same partitioning assumption as resume-play).
 *
 * Two modes (FUL-244):
 *  - **Offline/mock** (default): localStorage-backed, no backend required. This
 *    is the static-host path (Cloudflare Pages, python http.server, etc.).
 *  - **Live**: when the backend HTTP server is reachable (`initLive()`), the
 *    store hydrates from the API on boot and mutations sync to the backend
 *    (fire-and-forget). The views still read synchronously from memory.
 *
 * The store interface mirrors the REST CRUD shape so a live source can be
 * swapped in without touching the views.
 */
import type {
  AspectRatio,
  CreateVslRequest,
  Overlay,
  VslProject,
} from "../../contracts/src/contracts";
import { DEFAULT_PROGRESS_BAR_CONFIG } from "../../contracts/src/contracts";
import { createDefaultOverlays } from "../../contracts/src/overlay-templates";
import {
  DEFAULT_AUTOPLAY,
  DEFAULT_BRAND_COLOR,
  DEFAULT_CTA,
  DEFAULT_EMIT_ANALYTICS_TO_PARENT,
  DEFAULT_RESUME,
  createVslProject,
} from "../../backend/src/vsl-factory";
import { FIXTURE_MP4 } from "../../contracts/src/mocks";

const STORAGE_KEY = "fullvsl:app:projects";

const LEGACY_OVERLAY_COPY: Record<string, string> = {
  "Watch the free presentation": "Assista à apresentação gratuita",
  "▶ Play": "▶ Reproduzir",
  "Tap for sound": "Toque para ativar o som",
  "Muted autoplay in progress": "Reprodução automática sem som",
  "🔊 Unmute": "🔊 Ativar som",
  "Wait! Don't miss this.": "Espere! Não perca isto.",
  "Continue watching or jump to the offer.": "Continue assistindo ou vá direto para a oferta.",
  "▶ Continue": "▶ Continuar",
  "Claim your spot": "Garanta sua vaga",
  "Get access now →": "Acessar agora →",
  "You're all set!": "Tudo pronto!",
  "Ready to take the next step?": "Pronto para dar o próximo passo?",
};

function translateLegacyOverlayCopy(overlays: Overlay[]): Overlay[] {
  return overlays.map((overlay) => ({
    ...overlay,
    content: {
      ...overlay.content,
      text: overlay.content.text ? (LEGACY_OVERLAY_COPY[overlay.content.text] ?? overlay.content.text) : undefined,
      subtext: overlay.content.subtext ? (LEGACY_OVERLAY_COPY[overlay.content.subtext] ?? overlay.content.subtext) : undefined,
      buttonLabel: overlay.content.buttonLabel
        ? (LEGACY_OVERLAY_COPY[overlay.content.buttonLabel] ?? overlay.content.buttonLabel)
        : undefined,
    },
  }));
}

/** Upgrade projects created before playback settings became per-VSL. */
function normalizeProject(project: VslProject): VslProject {
  const isSample = project.id === "vsl-sample";
  const isVerticalSample = project.id === "vsl-mobile";
  return {
    ...project,
    name:
      isSample && project.name === "Q3 Product Launch VSL"
        ? "VSL de lançamento do produto"
        : isVerticalSample && project.name === "Mobile Reels Teaser (9:16)"
          ? "VSL vertical para redes sociais (9:16)"
          : project.name,
    ctaHeadline:
      isSample && project.ctaHeadline === "Wait — before you go"
        ? "Espere — antes de sair"
        : project.ctaHeadline,
    ctaSubtext:
      isSample && project.ctaSubtext === "Claim the offer mentioned in the video."
        ? "Aproveite a oferta apresentada no vídeo."
        : project.ctaSubtext,
    ctaButtonLabel:
      isSample && project.ctaButtonLabel === "Get access now"
        ? "Acessar agora"
        : isVerticalSample && project.ctaButtonLabel === "Swipe up →"
          ? "Deslize para cima →"
          : project.ctaButtonLabel,
    overlays: translateLegacyOverlayCopy(project.overlays ?? []),
    autoplay: { ...DEFAULT_AUTOPLAY, ...project.autoplay },
    progressBar: { ...DEFAULT_PROGRESS_BAR_CONFIG, ...project.progressBar },
    resume: { ...DEFAULT_RESUME, ...project.resume },
    brandColor: project.brandColor ?? DEFAULT_BRAND_COLOR,
    emitAnalyticsToParent:
      project.emitAnalyticsToParent ?? DEFAULT_EMIT_ANALYTICS_TO_PARENT,
  };
}

/** Seed a sample VSL preloaded with the fixture MP4 + MVP defaults. */
function seedSampleVsl(): VslProject {
  const project = createVslProject({
    name: "VSL de lançamento do produto",
    sourceType: "fullfunnel_media",
    sourceUrl: FIXTURE_MP4,
    aspectRatio: "auto",
    autoplay: { ...DEFAULT_AUTOPLAY },
    cta: {
      ...DEFAULT_CTA,
      revealAt: 25,
      destination: { type: "url", value: "https://example.com/offer" },
    },
    resume: { ...DEFAULT_RESUME },
  });
  project.id = "vsl-sample";
  project.status = "published";
  project.overlays = createDefaultOverlays();
  project.ctaHeadline = "Espere — antes de sair";
  project.ctaSubtext = "Aproveite a oferta apresentada no vídeo.";
  project.ctaButtonLabel = "Acessar agora";
  project.publish = {
    embedScript: "/embed/fullvsl.js",
    targetUrl: "https://example.com/funnel/q3-launch",
  };
  return project;
}

/** Seed a second demo VSL (9:16 vertical for social / Reels-style). */
function seedVerticalVsl(): VslProject {
  const project = createVslProject({
    name: "VSL vertical para redes sociais (9:16)",
    sourceType: "direct_url",
    sourceUrl: FIXTURE_MP4,
    aspectRatio: "9:16",
    autoplay: { ...DEFAULT_AUTOPLAY },
    cta: {
      ...DEFAULT_CTA,
      revealAt: 40,
      destination: { type: "url", value: "https://example.com/mobile-offer" },
    },
    resume: { ...DEFAULT_RESUME, mode: "always_resume" },
  });
  project.id = "vsl-mobile";
  project.status = "draft";
  project.overlays = createDefaultOverlays();
  project.ctaButtonLabel = "Deslize para cima →";
  return project;
}

/**
 * In-memory project store with optional localStorage persistence.
 *
 * The store is a thin Map wrapper. It emits a change event so the router/views
 * can re-render after mutations. This mirrors the REST CRUD surface so a live
 * HTTP source can replace it with zero view changes.
 *
 * Live mode (FUL-244): `initLive()` hydrates from the backend API and mutations
 * sync fire-and-forget. Views stay synchronous — they read from memory.
 */
export class VslStore {
  private readonly projects = new Map<string, VslProject>();
  private readonly seqs = new Map<string, number>();
  private readonly listeners = new Set<() => void>();
  private readonly persist: boolean;
  private nextSeq = 0;
  /** True when the backend API was detected and the store hydrated from it. */
  live = false;

  constructor(opts: { persist?: boolean; seed?: VslProject[] } = {}) {
    this.persist = opts.persist ?? true;
    const seed = opts.seed ?? null;
    if (seed) {
      for (const p of seed) {
        this.projects.set(p.id, normalizeProject(p));
        this.seqs.set(p.id, this.nextSeq++);
      }
    } else if (this.persist) {
      this.loadFromStorage();
    }
    if (this.projects.size === 0) {
      for (const p of [seedSampleVsl(), seedVerticalVsl()]) {
        this.projects.set(p.id, p);
        this.seqs.set(p.id, this.nextSeq++);
      }
      this.saveToStorage();
    }
  }

  /**
   * Hydrate the store from the backend API when it is available (FUL-244 live mode).
   * Replaces the in-memory projects with the live list and marks `live = true`.
   * If the fetch fails, the store stays in offline/mock mode.
   */
  async initLive(): Promise<boolean> {
    try {
      const { apiListVsls } = await import("./api-client");
      const remote = await apiListVsls();
      if (remote.length > 0) {
        this.projects.clear();
        this.seqs.clear();
        this.nextSeq = 0;
        for (const p of remote) {
          this.projects.set(p.id, normalizeProject(p));
          this.seqs.set(p.id, this.nextSeq++);
        }
        this.live = true;
        this.notify();
        return true;
      }
    } catch {
      // Backend not reachable — stay in offline/mock mode.
    }
    return false;
  }

  /** Return all projects (newest first by updatedAt, then by insertion order). */
  list(): VslProject[] {
    return [...this.projects.entries()].sort((a, b) => {
      const ta = new Date(a[1].updatedAt).getTime();
      const tb = new Date(b[1].updatedAt).getTime();
      if (tb !== ta) return tb - ta;
      return (this.seqs.get(b[0]) ?? 0) - (this.seqs.get(a[0]) ?? 0);
    }).map(([, p]) => p);
  }

  /** Get a single project by id, or undefined. */
  get(id: string): VslProject | undefined {
    return this.projects.get(id);
  }

  /** Create a new VSL project from a create request. Returns the new project. */
  create(req: CreateVslRequest): VslProject {
    const project = createVslProject(req);
    project.overlays = createDefaultOverlays();
    this.projects.set(project.id, project);
    this.seqs.set(project.id, this.nextSeq++);
    this.notify();
    // Live sync: push to the backend (fire-and-forget).
    if (this.live) {
      this.syncCreate(req).catch(() => {});
    }
    return project;
  }

  /** Patch a project's top-level fields. Returns the updated project or null. */
  update(id: string, patch: Partial<VslMutableFields>): VslProject | null {
    const p = this.projects.get(id);
    if (!p) return null;
    const updated = { ...p, ...patch, updatedAt: new Date().toISOString() };
    this.projects.set(id, updated);
    this.seqs.set(id, this.nextSeq++);
    this.notify();
    // Live sync: push to the backend (fire-and-forget).
    if (this.live) {
      this.syncUpdate(id, patch).catch(() => {});
    }
    return updated;
  }

  /** Replace the overlays array for a project. */
  setOverlays(id: string, overlays: Overlay[]): VslProject | null {
    return this.update(id, { overlays });
  }

  /** Delete a project by id. */
  remove(id: string): boolean {
    const existed = this.projects.delete(id);
    this.seqs.delete(id);
    if (existed) this.notify();
    // Live sync: push to the backend (fire-and-forget).
    if (this.live && existed) {
      import("./api-client").then(({ apiDeleteVsl }) => apiDeleteVsl(id)).catch(() => {});
    }
    return existed;
  }

  /** Subscribe to store changes (returns an unsubscribe function). */
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    this.saveToStorage();
    for (const fn of this.listeners) fn();
  }

  private async syncCreate(req: CreateVslRequest): Promise<void> {
    const { apiCreateVsl } = await import("./api-client");
    const created = await apiCreateVsl(req);
    // Update the in-memory entry with the server-assigned id/timestamps.
    const local = this.projects.get(req.name);
    // No-op if the optimistic entry was already replaced; the list re-syncs on
    // the next loadFromApi. This is intentionally lossy — the server is truth.
    if (local) {
      this.projects.delete(local.id);
      this.projects.set(created.id, normalizeProject(created));
      this.seqs.set(created.id, this.nextSeq++);
    }
  }

  private async syncUpdate(id: string, patch: Partial<VslMutableFields>): Promise<void> {
    const { apiUpdateVsl } = await import("./api-client");
    await apiUpdateVsl(id, patch).catch(() => {});
  }

  private loadFromStorage(): void {
    if (!this.persist) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as VslProject[];
      for (const p of parsed) this.projects.set(p.id, normalizeProject(p));
    } catch {
      // Storage unavailable (private mode, partitioned, etc.) — degrade to memory.
    }
  }

  private saveToStorage(): void {
    if (!this.persist) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.list()));
    } catch {
      // Best-effort; no-op on quota / partitioned-storage failures.
    }
  }
}

/** Fields that can be patched via `update`. */
export interface VslMutableFields {
  name?: string;
  status?: "draft" | "published";
  sourceType?: "fullfunnel_media" | "direct_url" | "youtube";
  sourceUrl?: string;
  aspectRatio?: AspectRatio;
  customRatio?: { w: number; h: number };
  autoplay?: VslProject["autoplay"];
  progressBar?: VslProject["progressBar"];
  cta?: VslProject["cta"];
  overlays?: Overlay[];
  resume?: VslProject["resume"];
  brandColor?: string;
  emitAnalyticsToParent?: boolean;
  ctaHeadline?: string;
  ctaSubtext?: string;
  ctaButtonLabel?: string;
  publish?: VslProject["publish"];
}

// Extend VslProject with optional CTA copy fields for the app shell's internal
// model (the player accepts these; the contract project doesn't carry them).
declare module "../../contracts/src/contracts" {
  interface VslProject {
    ctaHeadline?: string;
    ctaSubtext?: string;
    ctaButtonLabel?: string;
  }
}
